import { ApiResponse, GlobalStore, GridStore } from '@roc/feature-app-core';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import { ReferralsService } from '../services';
import { debounceRequest, GENERIC_ERROR_MESSAGE } from '@roc/feature-utils';
import { Option } from '@roc/ui/utils';
import { CancelToken } from 'axios';
import { OneToolService } from '../../../feature-one-tool/src/quote/services/oneToolService';


export class ReferralsStore {
  globalStore: GlobalStore;
  private referralsService: ReferralsService;
  private oneToolService: OneToolService;
  gridStore: GridStore;
  myReferrals: any;
  borroweReferrals: any;
  referrer: any;
  searchText: string;
  selectedReferrer: any;
  referrers: any[];
  options: Option[];
  createBorrower: boolean;
  currentConversationSid: any;
  chatDialogOpen: boolean;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.referralsService = new ReferralsService();
    this.oneToolService = new OneToolService();
    this.gridStore = new GridStore(() => this.fetchReferrals(), null);
    this.referrers = [];
    this.options = [];
    this.chatDialogOpen = false;

    makeObservable(this, {
      referrer: observable,
      myReferrals: observable,
      borroweReferrals: observable,
      searchText: observable,
      selectedReferrer: observable,
      referrers: observable,
      options: observable,
      createBorrower: observable,
      referralsCount: computed,
      fetchReferrerData: flow,
      getReferrer: flow,
      getReferrerForBorrower:flow,
      upsertReferrer: flow,
      findBorrowersAndLeadsByName: flow,
      currentConversationSid: observable,
      getConversationSidById: flow,
      chatDialogOpen: observable,
      setCurrentConversationSid: action,
      setChatDialogOpen: action
    });

    this.searchText = '';
    this.createBorrower = false;
  }

  fetchReferrals = async () => {
    try {
      console.log(this.globalStore.isCrmPortal);
      if (this.globalStore.isCrmPortal) {
        return await this.fetchBorrowerReferrals();
      } else {
        return await this.fetchMyReferrals();
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  };

  fetchMyReferrals = async () => {
    try {
      const response = await this.referralsService.getMyReferrals(
      this.gridStore.gridData.meta.pageNumber,
      this.gridStore.gridData.meta.pageSize,
      this.gridStore.gridData.meta.sortBy,
      this.gridStore.gridData.meta.sortDir,
      this.gridStore.gridData.meta.filters,
      this.gridStore.gridData.meta.dropdownFilters
      );
      this.myReferrals = response.data?.data?.rows;
      return response;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  };

  fetchBorrowerReferrals = async () => {
    try {
      const response = await this.referralsService.getBorrowerReferrals(
      this.gridStore.gridData.meta.pageNumber,
      this.gridStore.gridData.meta.pageSize,
      this.gridStore.gridData.meta.sortBy,
      this.gridStore.gridData.meta.sortDir,
      this.gridStore.gridData.meta.filters,
      this.gridStore.gridData.meta.dropdownFilters
      );
      console.log(response.data?.data?.rows);
      this.borroweReferrals = response.data?.data?.rows;
      return response;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  };

  getGridMeta = () => {
    const { pageNumber, pageSize, sortDir, sortBy, filters = {}, dropdownFilters = {} } = this.gridStore.gridData.meta;
    const start = (pageNumber - 1) * pageSize + 1;
    const combinedFilters = { ...filters, ...dropdownFilters };

    return { start, pageSize, sortDir, sortBy, combinedFilters };
  };

  get referralsCount() {
    return this.myReferrals?.length;
  }

  *upsertReferrer(leadId: number, companyId: number,referralType:string) {
    if (this.selectedReferrer) {
      yield this.referralsService.upsertReferrer(leadId, this.selectedReferrer, companyId, this.globalStore.lenderInfo?.lenderId,'',referralType,this.createBorrower);
      this.referrer = this.selectedReferrer;
      this.selectedReferrer = null;
      this.searchText = '';
    }
  }

  *fetchReferrerData(value, callback) {
    const recordComposedKey = value.split('-');
    const type = recordComposedKey[0];
    const id = recordComposedKey[1];
    const response = yield type === 'lead'
      ? this.oneToolService.fetchLeadData(id, '')
      : this.oneToolService.fetchBorrowerData(id);

    const recordData = response.data;
    this.selectedReferrer = { ...recordData, referrerType: type };
    callback && callback();
  }

  *getReferrer(leadId: number) {
    const response = yield this.referralsService.getReferrer(leadId);
    this.referrer = response.data?.data;
    this.searchText = this.referrer.fullName;
  }

  *getReferrerForBorrower(borrowerId: number) {
    const response = yield this.referralsService.getReferrerForBorrower(borrowerId);
    this.referrer = response.data?.data;
    this.searchText = this.referrer.fullName;
  }

  findBorrowersAndLeadsByNameDebounced = debounceRequest(
    (cancelToken, name: string) =>
      this.findBorrowersAndLeadsByName(name, cancelToken)
  );

  *findBorrowersAndLeadsByName(name: string, cancelToken: CancelToken) {
    try {
      if (name.length >= 3) {
        const response = yield this.oneToolService.findBorrowersAndLeadsByNameForRaf(
          name,
          cancelToken
        );
        this.options = response.data.data;
      } else {
        this.options = [];
      }
    } catch (e) {
      if (!e.isCancel) {
        this.globalStore.notificationStore.showErrorNotification({
          message: GENERIC_ERROR_MESSAGE,
        });
      }
    }
  }

  *getConversationSidById(referrerBorrowerId: any) {
    const response = yield this.referralsService.getConversationSid(referrerBorrowerId);
    this.currentConversationSid = response?.data?.data;
    if(this.currentConversationSid){
      this.setChatDialogOpen(true);
    }
  }

  setChatDialogOpen(value){
    this.chatDialogOpen = value;
  }

  setCurrentConversationSid(value){
    this.currentConversationSid = value;
  }
}
